/* eslint-disable @animech/axiom/no-references-in-top-level-values */
import { makeScene } from "@animech/axiom-format/src/definitions/definition-bindings";
import { root } from "./property-definitions";

export type CustomSceneTree = {
	//
};

export const customControllerTypes = ["rotate", "dragAndDrop"] as const;

export const scenes = ["2414718.json"] as const;

export const models = [
	"Table-B31-84",
	"Table-B31-170",
	"Table-B31-230",
	"Table-6B-60",
	"Table-9A-100",
	"Table-9A-120",
	"Table-B25A-120",
	"Table-HighTech-60",
	"Table-HighTech-110",
	"Table-Libelle-140",
	"Table-Libelle-70",
	"Table-Bovik-150",
	"Chair-A2",
	"Chair-HighTech",
	"Chair-Bovik",
	"Chair-Libelle",
	"Chair-1",
	"Bench-8",
	"Bench-9",
	"Bench-Libelle",
	"Sofa-3",
	"Sofa-5",
] as const;

export enum MaterialArea {
	Metal = "MA_Metal",
	Weld = "MA_MetalWeld",
	Wood = "MA_Wood1",
	Wood2 = "MA_Wood2",
	Endcap = "MA_WoodEndcap",
	ScrewAtlas = "MA_AtlasScrews",
	ScrewPhillips = "MA_ScrewRoundedPhillips-M3x10mm-A",
}

export const materials = [
	"WoodPineOiled",
	"WoodPineOiled-Endcap",

	"WoodOakOiled",
	"WoodOakOiled-Endcap",

	"WoodTeakUntreated",
	"WoodTeakUntreated-Endcap",

	"LacquerWhite",
	"LacquerGrey",
	"LacquerGraphite",
	"LacquerBlack",

	"PaintWoodWhite",
	"PaintWoodWhite-Endcap",
	"PaintWoodGrey",
	"PaintWoodGrey-Endcap",
	"PaintWoodGraphite",
	"PaintWoodGraphite-Endcap",
	"PaintWoodBlack",
	"PaintWoodBlack-Endcap",
	"PaintWoodRed",
	"PaintWoodRed-Endcap",
	"PaintWoodGreen",
	"PaintWoodGreen-Endcap",

	"LaminateWhite",
	"LaminateGrey",
	"LaminateGraphite",
	"LaminateBlack",

	"PaintMetalWhite",
	"PaintMetalWhite-Weld",
	"PaintMetalGrey",
	"PaintMetalGrey-Weld",
	"PaintMetalGraphite",
	"PaintMetalGraphite-Weld",
	"PaintMetalBlack",
	"PaintMetalBlack-Weld",
	"PaintMetalGreen",
	"PaintMetalGreen-Weld",
	"PaintMetalRed",
	"PaintMetalRed-Weld",

	"PaintMetalPowdercoatBlack",
	"PaintMetalPowdercoatBlack-Weld",
	"PaintMetalPowdercoatChestnut",
	"PaintMetalPowdercoatChestnut-Weld",
	"PaintMetalPowdercoatIvory",
	"PaintMetalPowdercoatIvory-Weld",
	"PaintMetalPowdercoatPetrol",
	"PaintMetalPowdercoatPetrol-Weld",

	"MetalGalvanizedSteel",
	"MetalGalvanizedSteel-Weld",
	"MetalGalvanizedZinc",
	"MetalGalvanizedZinc-Weld",

	"AtlasScrews",
	"AtlasScrews-Black",
	"ScrewRoundedPhillips-M3x10mm-A",
	"ScrewRoundedPhillips-M3x10mm-A-Black",
] as const;

export const scene = makeScene({
	camera: {
		settings: {
			fov: 37.8,
			farClip: 90,
			nearClip: 0.2,
			maxDistance: 10,
			minDistance: 3,
			floorPosition: 0,
			maxElevation: 65,
			minElevation: 0,
			minY: 0 + 0.2, // Buffer added to prevent seeing below the floor
			allowPan: true,
			allowOrbit: true,
			allowDolly: true,
			initialView: "initialStart",
			targetView: "initialEnd",
		},
		views: {
			initialStart: {
				orbitPosition: { xRotation: 25, yRotation: 15, distance: 3 },
				offset: { x: 0, y: 0, z: 0 },
			},
			initialEnd: {
				orbitPosition: { xRotation: 20, yRotation: 20, distance: 7 },
				offset: { x: 0, y: 0.5, z: 0 },
			},
			cart: {
				orbitPosition: { xRotation: 20, yRotation: 20, distance: 7 },
				offset: { x: 0, y: 0.5, z: 0 },
			},
		},
	},
	tree: {
		name: "root",
		children: [
			{
				name: "table",
				model: root.tableType,
				visible: root.tableVisible,
				transforms: [
					{
						type: "material",
						sourceMaterial: MaterialArea.Wood,
						targetMaterial: root.tableMaterialWood,
						transferAttributes: [],
					},
					{
						type: "material",
						sourceMaterial: MaterialArea.Metal,
						targetMaterial: root.tableMaterialMetal,
						transferAttributes: [],
					},
					{
						type: "material",
						sourceMaterial: MaterialArea.Weld,
						targetMaterial: root.tableMaterialWeld,
						transferAttributes: [],
					},
					{
						type: "material",
						sourceMaterial: MaterialArea.Endcap,
						targetMaterial: root.tableMaterialEndcap,
						transferAttributes: [],
					},
					{
						type: "material",
						sourceMaterial: MaterialArea.Wood2,
						targetMaterial: root.tableMaterialWood2,
						transferAttributes: [],
					},
					{
						type: "material",
						sourceMaterial: MaterialArea.ScrewAtlas,
						targetMaterial: root.tableMaterialScrewAtlas,
						transferAttributes: [],
					},
					{
						type: "material",
						sourceMaterial: MaterialArea.ScrewPhillips,
						targetMaterial: root.tableMaterialScrewPhillips,
						transferAttributes: [],
					},
				],
			},
			{
				name: "chair",
				template: root.chair,
				model: root.chair.chairType,
				visible: root.chair.visible,
				transforms: [
					{ type: "translate", vector: root.chair.pos },
					{ type: "rotate", vector: root.chair.rot },
					{
						type: "material",
						sourceMaterial: MaterialArea.Wood,
						targetMaterial: root.chair.chairMaterialWood,
						transferAttributes: [],
					},
					{
						type: "material",
						sourceMaterial: MaterialArea.Metal,
						targetMaterial: root.chair.chairMaterialMetal,
						transferAttributes: [],
					},
					{
						type: "material",
						sourceMaterial: MaterialArea.Weld,
						targetMaterial: root.chair.chairMaterialWeld,
						transferAttributes: [],
					},
					{
						type: "material",
						sourceMaterial: MaterialArea.Endcap,
						targetMaterial: root.chair.chairMaterialEndcap,
						transferAttributes: [],
					},
					{
						type: "material",
						sourceMaterial: MaterialArea.ScrewAtlas,
						targetMaterial: root.chair.chairMaterialScrewAtlas,
						transferAttributes: [],
					},
					{
						type: "material",
						sourceMaterial: MaterialArea.ScrewPhillips,
						targetMaterial: root.chair.chairMaterialScrewPhillips,
						transferAttributes: [],
					},
				],
			},
		],
	},
});
